import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PHASES } from "../lib/methodology";
import { toast } from "sonner";
import { Plus, ArrowRight, Trash2 } from "lucide-react";

export default function ProjectsPage() {
    const { user } = useAuth();
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [newProject, setNewProject] = useState({ name: "", description: "", assigned_user_ids: [] });
    const canCreate = user && ["company_manager", "happiness_manager", "project_manager", "super_admin"].includes(user.role);
    const canDelete = user && ["company_manager", "super_admin"].includes(user.role);

    const load = useCallback(
        () => api.get("/projects").then(({ data }) => setProjects(data)).catch((e) => toast.error(formatApiError(e))),
        []
    );

    useEffect(() => {
        load();
        if (canCreate) api.get("/users").then(({ data }) => setUsers(data)).catch(() => { /* optional */ });
    }, [canCreate, load]);

    const onCreate = async (e) => {
        e.preventDefault();
        try {
            await api.post("/projects", newProject);
            toast.success("Projeto criado");
            setShowCreate(false);
            setNewProject({ name: "", description: "", assigned_user_ids: [] });
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Eliminar projeto?")) return;
        try {
            await api.delete(`/projects/${id}`);
            toast.success("Projeto eliminado");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="space-y-8" data-testid="projects-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-caps">Área reservada</div>
                    <h1 className="font-display text-5xl text-[#0d1628] mt-4">Projetos</h1>
                    <p className="text-[#52525b] mt-3 max-w-xl">Gira os projetos de felicidade da sua organização. Cada projeto atravessa as 5 fases da metodologia IHTP.</p>
                </div>
                {canCreate && (
                    <button onClick={() => setShowCreate(!showCreate)} className="btn-primary" data-testid="new-project-btn">
                        <Plus size={16} /> Novo projeto
                    </button>
                )}
            </div>

            {showCreate && (
                <form onSubmit={onCreate} className="card-flat space-y-5" data-testid="create-project-form">
                    <div className="label-caps">Novo projeto</div>
                    <div>
                        <label className="label-caps block mb-2">Nome</label>
                        <input required className="input-clean" value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} data-testid="new-project-name" />
                    </div>
                    <div>
                        <label className="label-caps block mb-2">Descrição</label>
                        <textarea className="input-clean" rows={3} value={newProject.description} onChange={(e) => setNewProject({ ...newProject, description: e.target.value })} data-testid="new-project-description" />
                    </div>
                    {users.length > 0 && (
                        <div>
                            <label className="label-caps block mb-3">Atribuir colaboradores</label>
                            <div className="grid sm:grid-cols-2 gap-2">
                                {users.map((u) => {
                                    const checked = newProject.assigned_user_ids.includes(u.id);
                                    return (
                                        <label key={u.id} className="flex items-center gap-3 text-sm border border-[#e4e4e7] p-3 hover:border-[#0d1628] cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={(e) => {
                                                    const next = e.target.checked
                                                        ? [...newProject.assigned_user_ids, u.id]
                                                        : newProject.assigned_user_ids.filter((x) => x !== u.id);
                                                    setNewProject({ ...newProject, assigned_user_ids: next });
                                                }}
                                                data-testid={`assign-${u.id}`}
                                            />
                                            <span>{u.name} <span className="text-[#52525b]">· {u.role}</span></span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div className="flex gap-3">
                        <button type="submit" className="btn-primary" data-testid="create-project-submit">Criar</button>
                        <button type="button" className="btn-ghost" onClick={() => setShowCreate(false)}>Cancelar</button>
                    </div>
                </form>
            )}

            {projects.length === 0 ? (
                <div className="card-flat text-[#52525b]" data-testid="empty-projects">
                    Ainda não criou nenhum projeto. {canCreate && "Comece clicando em \"Novo projeto\"."}
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
                    {projects.map((p) => (
                        <article key={p.id} className="card-flat flex flex-col" data-testid={`project-card-${p.id}`}>
                            <div className="flex items-center gap-3">
                                <span className="phase-dot active">{p.current_phase || 1}</span>
                                <div className="label-caps">{PHASES[(p.current_phase || 1) - 1]?.title}</div>
                            </div>
                            <h2 className="font-display text-3xl text-[#0d1628] mt-5 leading-tight">{p.name}</h2>
                            {p.description && <p className="text-sm text-[#52525b] mt-3 line-clamp-3">{p.description}</p>}
                            <div className="mt-6 pt-5 border-t border-[#e4e4e7] flex items-center justify-between">
                                <Link to={`/app/projects/${p.id}`} className="btn-ghost" data-testid={`project-open-${p.id}`}>
                                    Abrir <ArrowRight size={14} />
                                </Link>
                                {canDelete && (
                                    <button onClick={() => onDelete(p.id)} className="text-[#52525b] hover:text-red-600 p-2" data-testid={`project-delete-${p.id}`}>
                                        <Trash2 size={16} />
                                    </button>
                                )}
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}
